import { DialogComponent } from '@theme/dialog';

const BOUTIQUE_AVAILABILITY_ENDPOINT = 'https://k-flowprocessor.julian-fashion.com/api/v1/boutique_availability';
const BOUTIQUE_AVAILABILITY_API_KEY = 'bak_DxIOHWg8rk2VzZGIw6X5_TgFDi4nHSVX6VS0ewLg1lU';

class BoutiqueAvailabilityDrawer extends DialogComponent {
  #requestId = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.#handleTriggerClick);
    this.refs.dialog?.addEventListener('click', this.#handleDrawerClick);
    this.refs.dialog?.addEventListener('change', this.#handleDrawerChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleTriggerClick);
    this.refs.dialog?.removeEventListener('click', this.#handleDrawerClick);
    this.refs.dialog?.removeEventListener('change', this.#handleDrawerChange);
  }

  #handleTriggerClick = (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('a[href*="#boutique-availability"]') : null;
    if (!trigger) return;

    event.preventDefault();
    this.#resetDrawer();
    this.showDialog();
  };

  #handleDrawerClick = (event) => {
    const close = event.target instanceof Element ? event.target.closest('[data-boutique-availability-close]') : null;
    if (close) this.closeDialog();
  };

  #handleDrawerChange = (event) => {
    if (!(event.target instanceof HTMLSelectElement)) return;
    if (!event.target.matches('[data-boutique-availability-size-select]')) return;

    this.#loadAvailability(event.target);
  };

  open() {
    this.#resetDrawer();
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  #resetDrawer() {
    this.#requestId += 1;

    const select = this.#sizeSelect;
    if (select) select.value = '';

    this.#setLoading(false);
    this.#setEmpty(false);
    this.#setOnlineOnly(false);
    this.#renderStores([]);
  }

  async #loadAvailability(select) {
    const selectedOption = select.selectedOptions[0];

    this.#setEmpty(false);
    this.#setOnlineOnly(false);
    this.#renderStores([]);

    if (!select.value || !selectedOption) {
      this.#setLoading(false);
      this.#setOnlineOnly(false);
      return;
    }

    const requestId = ++this.#requestId;
    this.#setLoading(true);

    try {
      const response = await this.#fetchAvailability({
        productId: this.dataset.productId || '',
        productHandle: this.dataset.productHandle || '',
        productSku: selectedOption.dataset.variantSku || this.dataset.productSku || '',
        locale: this.dataset.locale || document.documentElement.lang || '',
        language: this.dataset.language || (document.documentElement.lang || '').split('-')[0],
        country: this.dataset.country || '',
        size: select.value,
        variantId: selectedOption.dataset.variantId || '',
        optionValueId: selectedOption.dataset.optionValueId || '',
        selectedOptionAvailable: selectedOption.dataset.available !== 'false',
      });

      if (requestId !== this.#requestId) return;

      const stores = this.#getBoutiquesForSize(response, select.value);
      const hasStores = stores.length > 0;
      const isOnlineOnly = hasStores && stores.every((store) => !store.available);

      this.#setOnlineOnly(isOnlineOnly);
      this.#renderStores(isOnlineOnly ? [] : stores);
      this.#setEmpty(stores.length === 0);
    } catch (error) {
      if (requestId !== this.#requestId) return;
      this.#setOnlineOnly(false);
      this.#setEmpty(true);
    } finally {
      if (requestId === this.#requestId) this.#setLoading(false);
    }
  }

  async #fetchAvailability(request) {
    if (!request.productSku) {
      throw new Error('Product SKU is required to fetch boutique availability.');
    }

    const response = await fetch(BOUTIQUE_AVAILABILITY_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Api-Key': BOUTIQUE_AVAILABILITY_API_KEY,
      },
      body: JSON.stringify({
        product_sku: request.productSku,
        lang: request.language || 'it',
      }),
    });

    if (!response.ok) {
      throw new Error(`Boutique availability request failed with status ${response.status}.`);
    }

    return response.json();
  }

  #getBoutiquesForSize(response, selectedSize) {
    const sizes = Array.isArray(response?.sizes) ? response.sizes : [];
    const selectedSizeData = sizes.find((size) => String(size?.sizeId || '').toLowerCase() === selectedSize.toLowerCase());

    if (selectedSizeData && Array.isArray(selectedSizeData.boutiques)) {
      return selectedSizeData.boutiques;
    }

    if (Array.isArray(response?.boutiques)) {
      return response.boutiques;
    }

    return [];
  }

  #getAddressLines(address) {
    if (typeof address === 'string') {
      return address.split(' - ').filter(Boolean);
    }

    if (!address || typeof address !== 'object') {
      return [];
    }

    return [address.line1, address.line2].filter(Boolean);
  }

  #getMapsUrl(store, addressLines) {
    const explicitUrl = store.address?.maps_url || store.maps_url;
    if (explicitUrl) return explicitUrl;

    const query = addressLines.join(' ');
    if (!query) return '#';

    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  }

  #getPhoneUrl(phone) {
    const normalizedPhone = String(phone || '').replace(/[^\d+]/g, '');
    return normalizedPhone ? `tel:${normalizedPhone}` : '#';
  }

  #getOpeningHours(store) {
    const openingHours = Array.isArray(store.opening_hours)
      ? store.opening_hours
      : String(store.opening_hours || '')
          .split('\n')
          .filter(Boolean);

    if (store.closing_day) {
      openingHours.push(store.closing_day);
    }

    return openingHours;
  }

  #renderStores(stores) {
    const result = this.refs.dialog?.querySelector('[data-boutique-availability-result]');
    const storesContainer = this.refs.dialog?.querySelector('[data-boutique-availability-stores]');
    if (!(result instanceof HTMLElement) || !(storesContainer instanceof HTMLElement)) return;

    storesContainer.replaceChildren(...stores.map((store) => this.#createStoreElement(store)));
    result.hidden = stores.length === 0;
  }

  #createStoreElement(store) {
    const wrapper = document.createElement('div');
    wrapper.className = 'boutique-availability-drawer__store';

    const available = Boolean(store.available);
    const statusText = available ? this.#inStockLabel : this.#notAvailableLabel;
    const addressLines = this.#getAddressLines(store.address);
    const addressHtml = addressLines.map((line) => this.#escapeHtml(line)).join('<br>');
    const mapsUrl = this.#escapeAttribute(this.#getMapsUrl(store, addressLines));
    const phone = this.#escapeHtml(store.phone || '');
    const phoneUrl = this.#escapeAttribute(store.phone_url || this.#getPhoneUrl(store.phone));
    const openingHours = this.#getOpeningHours(store);

    wrapper.innerHTML = `
      <div class="boutique-availability-drawer__store-header">
        <h3 class="boutique-availability-drawer__store-name">${this.#escapeHtml(store.name || '')}</h3>
        <span class="boutique-availability-drawer__stock" data-available="${String(available)}">
          <span>${statusText}</span>
          <span class="boutique-availability-drawer__stock-dot" aria-hidden="true"></span>
        </span>
      </div>
      <address class="boutique-availability-drawer__address">
        <a href="${mapsUrl}" target="_blank" rel="noopener noreferrer">
          ${addressHtml}
        </a>
        <a href="${phoneUrl}">${phone}</a>
      </address>
      <div class="boutique-availability-drawer__hours">
        <h4>${this.#escapeHtml(this.#openingHoursTitle)}</h4>
        <p>${openingHours.map((item) => this.#escapeHtml(item)).join('<br>')}</p>
      </div>
    `;

    return wrapper;
  }

  #setLoading(isLoading) {
    const loading = this.refs.dialog?.querySelector('[data-boutique-availability-loading]');
    if (loading instanceof HTMLElement) loading.hidden = !isLoading;
  }

  #setEmpty(isEmpty) {
    const empty = this.refs.dialog?.querySelector('[data-boutique-availability-empty]');
    if (empty instanceof HTMLElement) empty.hidden = !isEmpty;
  }

  #setOnlineOnly(isOnlineOnly) {
    const onlineOnly = this.refs.dialog?.querySelector('[data-boutique-availability-online-only]');
    if (onlineOnly instanceof HTMLElement) onlineOnly.hidden = !isOnlineOnly;
  }

  #escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  #escapeAttribute(value) {
    return this.#escapeHtml(value);
  }

  get #sizeSelect() {
    const select = this.refs.dialog?.querySelector('[data-boutique-availability-size-select]');
    return select instanceof HTMLSelectElement ? select : null;
  }

  get #inStockLabel() {
    return this.dataset.statusInStock || 'In stock';
  }

  get #notAvailableLabel() {
    return this.dataset.statusNotAvailable || 'Not available';
  }

  get #openingHoursTitle() {
    return this.dataset.openingHoursTitle || 'Opening hours';
  }
}

if (!customElements.get('boutique-availability-drawer')) {
  customElements.define('boutique-availability-drawer', BoutiqueAvailabilityDrawer);
}
